// Send button with a schedule-send dropdown: presets (later today / tomorrow
// morning / next Monday) plus a custom date-time picker. Shared by the reply
// composer (inbox) and the new-message ComposeDialog.
import { useState } from 'react';
import { ChevronDown, Clock, Loader2, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export function SendSplitButton({
  disabled,
  loading,
  primaryLabel,
  onSendNow,
  onSchedule,
}: {
  disabled?: boolean;
  loading?: boolean;
  primaryLabel: string;
  onSendNow: () => void;
  onSchedule: (when: Date) => void;
}) {
  const [open, setOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [custom, setCustom] = useState(() => {
    // Default to tomorrow 9am local.
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
    return toDatetimeLocal(d);
  });

  const opts = scheduleOptions();

  return (
    <div className="inline-flex">
      <Button
        size="sm"
        disabled={disabled}
        onClick={onSendNow}
        className="gap-1.5 rounded-r-none"
      >
        {loading ? (
          <Loader2 className="size-3.5 animate-spin" strokeWidth={2} />
        ) : (
          <Send className="size-3.5" strokeWidth={2} />
        )}
        {primaryLabel}
      </Button>
      <DropdownMenu open={open} onOpenChange={(o) => { setOpen(o); if (!o) setCustomOpen(false); }}>
        <DropdownMenuTrigger asChild>
          <Button
            size="sm"
            disabled={disabled}
            className="rounded-l-none border-l border-primary-foreground/20 px-2"
            aria-label="Send options"
          >
            <ChevronDown className="size-3.5" strokeWidth={2.5} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          {!customOpen && (
            <>
              <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Schedule send
              </div>
              {opts.map((o) => (
                <DropdownMenuItem
                  key={o.label}
                  onClick={() => {
                    setOpen(false);
                    onSchedule(o.when);
                  }}
                  className="flex items-center justify-between gap-3"
                >
                  <span>{o.label}</span>
                  <span className="text-[10px] text-muted-foreground">{o.sub}</span>
                </DropdownMenuItem>
              ))}
              <div className="border-t" />
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  setCustomOpen(true);
                }}
                className="gap-2"
              >
                <Clock className="size-3.5" strokeWidth={1.75} />
                Pick date &amp; time…
              </DropdownMenuItem>
            </>
          )}
          {customOpen && (
            <div className="space-y-2 p-2">
              <input
                type="datetime-local"
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                className="w-full rounded-md border bg-background px-2 py-1 text-sm outline-none focus:ring-1 focus:ring-ring"
              />
              <div className="flex justify-end gap-1">
                <Button variant="ghost" size="xs" onClick={() => setCustomOpen(false)}>
                  Cancel
                </Button>
                <Button
                  size="xs"
                  onClick={() => {
                    const d = new Date(custom);
                    if (!Number.isNaN(d.getTime()) && d.getTime() > Date.now() + 35_000) {
                      setOpen(false);
                      onSchedule(d);
                    }
                  }}
                >
                  Schedule
                </Button>
              </div>
            </div>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export function scheduleOptions(): { label: string; sub: string; when: Date }[] {
  const out: { label: string; sub: string; when: Date }[] = [];
  const now = new Date();
  const today6pm = new Date(now);
  today6pm.setHours(18, 0, 0, 0);
  if (today6pm.getTime() > now.getTime() + 30 * 60_000) {
    out.push({ label: 'Later today', sub: formatTimeShort(today6pm), when: today6pm });
  }
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(9, 0, 0, 0);
  out.push({ label: 'Tomorrow morning', sub: formatTimeShort(tomorrow, true), when: tomorrow });
  const nextMonday = new Date(now);
  const daysUntilMonday = (8 - now.getDay()) % 7 || 7;
  nextMonday.setDate(now.getDate() + daysUntilMonday);
  nextMonday.setHours(9, 0, 0, 0);
  out.push({ label: 'Next Monday', sub: formatTimeShort(nextMonday, true), when: nextMonday });
  return out;
}

export function formatTimeShort(d: Date, withDate = false): string {
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  if (!withDate) return time;
  const date = d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  return `${date} · ${time}`;
}

export function formatSendAt(iso: string): string {
  const d = new Date(iso);
  const sameDay = d.toDateString() === new Date().toDateString();
  if (sameDay) return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return d.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Format a Date as 'YYYY-MM-DDTHH:mm' in local time for <input type=datetime-local>. */
export function toDatetimeLocal(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
